import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Form, Spinner } from "react-bootstrap";
import { useApp } from "app/contexts/AppContext";
import { request } from "utils/request";
import "./bookingLinkPopover.css";

const slugify = (text) => {
  return (text || "")
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, "")
    .replace(/[\s_]+/g, "-")
    .replace(/-+/g, "-");
};

const BookingLinkPopover = ({ show, onClose }) => {
  const navigate = useNavigate();
  const {
    user: { profile },
  } = useApp();

  const [schedulingData, setSchedulingData] = useState(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState(null);

  const fetchSchedulingProfile = useCallback(async () => {
    setError(null);
    setSchedulingData(null);
    try {
      const data = await request(
        `${process.env.REACT_APP_API_URL}/scheduling/profile/`
      );
      setSchedulingData(data);
    } catch (err) {
      setError("Unable to load booking link");
    }
  }, []);

  useEffect(() => {
    if (show) {
      fetchSchedulingProfile();
      setCopied(false);
    } else {
      setSchedulingData(null);
      setError(null);
    }
  }, [show, fetchSchedulingProfile]);

  const loading = show && !schedulingData && !error;

  if (!show) return null;
  const companySlug = slugify(
    profile?.company?.name ||
      `${profile?.first_name || ""}-${profile?.last_name || ""}`
  );
  const bookingUrl =
    schedulingData?.scheduling_hash && companySlug
      ? `${window.location.origin}/booking/${companySlug}/${schedulingData.scheduling_hash}`
      : "";

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(bookingUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    } catch {
      const textArea = document.createElement("textarea");
      textArea.value = bookingUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    }
  };

  const handleGoToSettings = () => {
    onClose();
    navigate("/client-booking");
  };

  const isActive = schedulingData?.is_active;

  return (
    <div className="booking-link-popover">
      {loading ? (
        <div className="booking-link-loading">
          <Spinner animation="border" size="sm" variant="primary" />
        </div>
      ) : error ? (
        <div>
          <p className="text-muted mb-0">{error}</p>
        </div>
      ) : isActive ? (
        <>
          <div className="booking-link-popover-title">Client Booking Link</div>
          <div className="booking-link-popover-subtitle">
            Share or add it to your website to get booked!
          </div>
          <div className="booking-link-url-row">
            <Form.Control
              type="text"
              value={bookingUrl}
              readOnly
              size="sm"
              className="booking-link-url-input"
            />
            <Button
              variant="primary"
              size="sm"
              onClick={handleCopy}
              className="booking-link-copy-btn"
            >
              {copied ? "Copied" : "Copy Link"}
            </Button>
          </div>
        </>
      ) : (
        <>
          <div className="booking-link-popover-title">
            Booking Link is inactive
          </div>
          <div className="booking-link-popover-subtitle">
            You are currently not accepting client bookings. Go to settings to
            turn it on.
          </div>
          <div className="text-end">
            <Button
              variant="outline-primary"
              size="sm"
              onClick={handleGoToSettings}
            >
              Go to Settings
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

export default BookingLinkPopover;
